"use client";

import { AlanaLogo } from "@/components/ui/AlanaLogo";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";

export default function AuthLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const t = useTranslations("auth.layout");
  const headline = t("headline");
  const accent = t("headlineAccent");
  const accentColor = "#ff8086";

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel — hidden on mobile */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] flex-shrink-0 relative overflow-hidden">
        {/* Dark background */}
        <div className="absolute inset-0" style={{ background: "#0a0a0a" }} />

        {/* Subtle dot grid pattern overlay */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
            opacity: 0.18,
          }}
        />

        {/* Ambient aurora — looping color blobs (DLS palette) */}
        {/* Primary coral blob — bottom-left, dominant */}
        <motion.div
          className="absolute w-[600px] h-[600px] rounded-full blur-[140px]"
          style={{ bottom: "-120px", left: "-80px" }}
          animate={{
            opacity: [0.6, 0.9, 0.7, 1, 0.6],
            x: [0, 80, 30, -40, 0],
            y: [0, -60, 40, -30, 0],
            scale: [1, 1.2, 0.9, 1.15, 1],
            background: [
              "rgba(255, 128, 134, 0.45)",
              "rgba(148, 139, 255, 0.4)",
              "rgba(255, 128, 134, 0.5)",
              "rgba(125, 228, 255, 0.35)",
              "rgba(255, 128, 134, 0.45)",
            ],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        {/* Secondary lavender blob — top-right */}
        <motion.div
          className="absolute w-[500px] h-[500px] rounded-full blur-[120px]"
          style={{ top: "-80px", right: "-60px" }}
          animate={{
            opacity: [0.35, 0.65, 0.45, 0.7, 0.35],
            x: [0, -70, 30, -50, 0],
            y: [0, 70, -30, 50, 0],
            scale: [1, 0.85, 1.15, 0.9, 1],
            background: [
              "rgba(148, 139, 255, 0.35)",
              "rgba(255, 128, 134, 0.35)",
              "rgba(64, 227, 198, 0.35)",
              "rgba(148, 139, 255, 0.35)",
              "rgba(148, 139, 255, 0.35)",
            ],
          }}
          transition={{
            duration: 16,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        {/* Tertiary mint blob — center, drifting */}
        <motion.div
          className="absolute w-[350px] h-[350px] rounded-full blur-[100px]"
          style={{ top: "35%", left: "25%" }}
          animate={{
            opacity: [0.25, 0.5, 0.3, 0.55, 0.25],
            x: [0, 60, -40, 30, 0],
            y: [0, -40, 50, -30, 0],
            background: [
              "rgba(64, 227, 198, 0.3)",
              "rgba(255, 225, 129, 0.3)",
              "rgba(148, 139, 255, 0.3)",
              "rgba(64, 227, 198, 0.35)",
              "rgba(64, 227, 198, 0.3)",
            ],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div>
            <AlanaLogo variant="full" white className="h-7" />
          </div>

          {/* Animated headline */}
          <motion.h2
            className="text-4xl font-bold text-white leading-tight"
            style={{ fontFamily: "MuseoModerno, sans-serif" }}
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.12 } },
            }}
          >
            {headline.split(" ").map((word, i) => (
              <motion.span
                key={i}
                className="inline-block mr-[0.3em]"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                {word}
              </motion.span>
            ))}
            <br />
            {accent.split(" ").map((word, i) => (
              <motion.span
                key={`accent-${i}`}
                className="inline-block mr-[0.3em]"
                style={{ color: accentColor }}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                {word}
              </motion.span>
            ))}
          </motion.h2>

          {/* Footer */}
          <p className="text-white/30 text-xs lowercase">{t("footer")}</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 relative overflow-hidden overflow-y-auto bg-[var(--background)] flex items-center justify-center">
        <div className="w-full max-w-[480px] lg:max-w-[600px] mx-auto px-6 py-10 relative z-10">
          {children}
        </div>
      </div>
    </div>
  );
}
